import React, { forwardRef, useEffect, useRef } from 'react';
import { Ripple } from 'primereact/ripple';
import { Tooltip } from 'primereact/tooltip';
import { ObjectUtils, classNames, IconUtils } from 'primereact/utils';

/**
 * Lightweight copy of PrimeReact's Button that relies on JS default parameters
 * instead of deprecated `defaultProps` on memo components (React 19 warning).
 */
const PrimeButton = React.memo(forwardRef((props, ref) => {
  const {
    label = null,
    icon = null,
    iconPos = 'left',
    badge = null,
    badgeClassName = null,
    tooltip = null,
    tooltipOptions = null,
    disabled: disabledProp = false,
    loading = false,
    loadingIcon = 'pi pi-spinner pi-spin',
    visible = true,
    children,
    className,
    ...otherProps
  } = props;

  const elementRef = useRef(null);

  useEffect(() => {
    ObjectUtils.combinedRefs(elementRef, ref);
  }, [ref]);

  if (visible === false) {
    return null;
  }

  const createIcon = () => {
    const resolvedIcon = loading ? loadingIcon : icon;
    const iconClassName = classNames(
      'p-button-icon p-c',
      {
        'p-button-loading-icon': loading,
        [`p-button-icon-${iconPos}`]: label
      }
    );

    return IconUtils.getJSXIcon(
      resolvedIcon,
      { className: iconClassName },
      { props }
    );
  };

  const createLabel = () => {
    if (label) {
      return (
        <span className="p-button-label p-c">
          {label}
        </span>
      );
    }

    if (!children) {
      return (
        <span
          className="p-button-label p-c"
          dangerouslySetInnerHTML={{ __html: '&nbsp;' }}
        />
      );
    }

    return null;
  };

  const createBadge = () => {
    if (!badge) return null;

    return (
      <span className={classNames('p-badge', badgeClassName)}>
        {badge}
      </span>
    );
  };

  const disabled = disabledProp || loading;
  const showTooltip = !disabled || (tooltipOptions && tooltipOptions.showOnDisabled);
  const hasTooltip = ObjectUtils.isNotEmpty(tooltip) && showTooltip;
  const buttonClassName = classNames(
    'p-button p-component',
    className,
    {
      'p-button-icon-only': (icon || (loading && loadingIcon)) && !label && !children,
      'p-button-vertical': (iconPos === 'top' || iconPos === 'bottom') && label,
      'p-disabled': disabled,
      'p-button-loading': loading,
      'p-button-loading-label-only': loading && !icon && label,
      [`p-button-loading-${iconPos}`]: loading && loadingIcon && label
    }
  );
  const iconElement = createIcon();
  const labelElement = createLabel();
  const badgeElement = createBadge();
  const defaultAriaLabel = label
    ? `${label}${badge ? ` ${badge}` : ''}`
    : props['aria-label'];

  return (
    <>
      <button
        ref={elementRef}
        aria-label={defaultAriaLabel}
        {...otherProps}
        className={buttonClassName}
        disabled={disabled}
      >
        {iconElement}
        {labelElement}
        {children}
        {badgeElement}
        <Ripple />
      </button>
      {hasTooltip && (
        <Tooltip target={elementRef} content={tooltip} {...tooltipOptions} />
      )}
    </>
  );
}));

PrimeButton.displayName = 'PrimeButton';

export default PrimeButton;
